import type { Answers } from '@sss/form-engine';
import type { OutboxStore } from '@sss/sync';
import type { SurveyStatus } from '@sss/workflow';
import type { CarriedFinding, PackCustomer, PackSite, PackTemplateVersion, SyncPack } from '../api/types';

/** A survey being captured on this device. `id` is the survey's permanent UUID (ADR-0024). */
export interface DraftRecord {
  id: string;
  siteId: string;
  customerId: string;
  surveyType: string;
  templateVersionId: string;
  status: SurveyStatus;
  /** Assigned by the server on first sync. */
  humanId: string | null;
  /** Short local reference shown until the human id arrives ("D-7F3A"). */
  localRef: string;
  surveyDate: string | null;
  answers: Answers;
  /** Analyst notes for a returned survey: controlCode|fieldKey → note. Only these are editable. */
  flags: Record<string, string>;
  reporterUserId: string;
  createdAt: number;
  updatedAt: number;
  /** local = never sent; pending = in the outbox; synced = server has latest; rejected = needs attention */
  syncState: 'local' | 'pending' | 'synced' | 'rejected';
  rejectedReason?: string;
  /** What came with the rejection, e.g. `{ issues }` for VALIDATION_FAILED (listed with links). */
  rejectedDetails?: unknown;
  /** Files the server refused for good (type/size): removed from the answers, shown until dismissed. */
  refusedFiles?: { fileName: string; ref: string; code: string }[];
  /**
   * The server took this survey away from the device: cancelled, or given to
   * another reporter. It can't be edited or sent any more — only removed.
   */
  withdrawn?: { reason: WithdrawnReason; note?: string | null };
}

export type WithdrawnReason = 'SURVEY_CANCELLED' | 'SURVEY_REASSIGNED';
export const WITHDRAWN_REASONS: readonly string[] = ['SURVEY_CANCELLED', 'SURVEY_REASSIGNED'];

export type AttachmentKind = 'PHOTO' | 'FILE' | 'SIGNATURE';

export interface AttachmentRecord {
  id: string;
  /** The survey, or for an event report photo the report id (ADR-0029; never matches a draft). */
  surveyId: string;
  /** Set for event report photos: uploaded with `eventId` instead of `surveyId`. */
  eventId?: string;
  kind: AttachmentKind;
  /** Control code or field key the file belongs to. */
  ref: string;
  /** Stored as bytes (portable across IndexedDB and SQLite). */
  data: ArrayBuffer;
  contentType: string;
  fileName: string;
  size: number;
  lat?: number | null;
  lng?: number | null;
  takenAt?: string | null;
  uploaded: boolean;
  createdAt: number;
}

/**
 * Everything the app keeps on the device. Implemented over IndexedDB (PWA)
 * and encrypted SQLite (store apps). It is also the outbox's store.
 */
export interface OfflineStorage extends OutboxStore {
  readonly kind: 'indexeddb' | 'sqlite';
  init(): Promise<void>;

  // Offline pack
  replacePack(pack: SyncPack): Promise<void>;
  getSite(id: string): Promise<PackSite | undefined>;
  listSites(): Promise<PackSite[]>;
  searchSites(query: string, limit?: number): Promise<PackSite[]>;
  putSites(sites: PackSite[]): Promise<void>;
  getCustomer(id: string): Promise<PackCustomer | undefined>;
  putCustomers(customers: PackCustomer[]): Promise<void>;
  getTemplateVersion(versionId: string): Promise<PackTemplateVersion | undefined>;
  putTemplateVersion(t: PackTemplateVersion): Promise<void>;
  findingsForSite(siteId: string): Promise<CarriedFinding[]>;

  // Drafts
  putDraft(d: DraftRecord): Promise<void>;
  getDraft(id: string): Promise<DraftRecord | undefined>;
  listDrafts(): Promise<DraftRecord[]>;
  deleteDraft(id: string): Promise<void>;

  // Attachments
  putAttachment(a: AttachmentRecord): Promise<void>;
  getAttachment(id: string): Promise<AttachmentRecord | undefined>;
  listAttachments(surveyId: string): Promise<AttachmentRecord[]>;
  deleteAttachment(id: string): Promise<void>;

  // Small key/value cache (last pack time, cached /me, branding…)
  getMeta<T>(key: string): Promise<T | undefined>;
  setMeta(key: string, value: unknown): Promise<void>;

  /** Remove everything (account disabled / sign-out with wipe). */
  wipe(): Promise<void>;
}

export function siteMatches(site: PackSite, query: string): boolean {
  const q = normalise(query);
  if (!q) return true;
  return [site.name, site.code, site.customerName ?? '', site.address ?? '', site.branchCode ?? ''].some((v) => normalise(v).includes(q));
}

/** Lower-case, strip accents, so "chia" finds "Chía". */
export function normalise(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}
